<template>
  <div class="card shadow-sm table-card mb-4">
    <div class="form-card-header flex-wrap">
      <div class="section-number"><i class="fe fe-package" style="font-size: 12px;"></i></div>
      <div class="flex-grow-1">
        <h6 class="mb-0 font-weight-bold text-white">Top services and drugs</h6>
        <small style="color: rgba(255,255,255,.75);">Claim lines in the period and filters above (drafts excluded)</small>
      </div>
      <div class="btn-group btn-group-sm" role="group" aria-label="Services or drugs">
        <button type="button" class="btn" :class="tab === 'services' ? 'btn-light' : 'btn-outline-light'" @click="tab = 'services'">Services</button>
        <button type="button" class="btn" :class="tab === 'drugs' ? 'btn-light' : 'btn-outline-light'" @click="tab = 'drugs'">Drugs</button>
      </div>
    </div>
    <div class="card-body">
      <div class="d-flex flex-wrap align-items-center mb-3" style="gap: 8px;">
        <label class="small text-muted mb-0">Rank by</label>
        <select class="form-control form-control-sm" style="width: auto;" v-model="sort" @change="load">
          <option value="amount">Amount claimed</option>
          <option value="approved">Amount approved</option>
          <option value="lines">Times claimed (lines)</option>
          <option value="claims">Number of claims</option>
        </select>
        <select class="form-control form-control-sm" style="width: auto;" v-model.number="limit" @change="load" aria-label="How many">
          <option :value="10">Top 10</option><option :value="20">Top 20</option><option :value="50">Top 50</option><option :value="100">Top 100</option>
        </select>
        <button class="btn btn-sm btn-outline-success ml-auto" @click="exportCsv" :disabled="exporting">
          <i class="fe fe-download mr-1"></i> {{ exporting ? "Exporting…" : "Export CSV" }}
        </button>
      </div>

      <div v-if="loading && !data" class="empty-state"><i class="mdi mdi-loading mdi-spin"></i>Loading…</div>
      <div v-else-if="error" class="empty-state"><i class="fe fe-alert-triangle"></i>{{ error }}</div>
      <template v-else-if="current">
        <div class="d-flex flex-wrap mb-3" style="gap: 22px;">
          <div><span class="stat-label">{{ tab === "drugs" ? "Drug lines" : "Service lines" }}</span><div class="stat-value">{{ num(totals.lines_count) }}</div></div>
          <div><span class="stat-label">Claimed</span><div class="stat-value">{{ money(totals.amount_claimed) }}</div></div>
          <div><span class="stat-label">Approved</span><div class="stat-value">{{ money(totals.amount_approved) }}</div></div>
          <div><span class="stat-label">Distinct {{ tab }}</span><div class="stat-value">{{ num(current.rows.length) }}{{ current.rows.length >= limit ? "+" : "" }}</div></div>
        </div>
        <div v-if="!current.rows.length" class="empty-state">No {{ tab }} were claimed in this period.</div>
        <template v-else>
          <apexchart type="bar" :height="Math.max(260, chartRows.length * 30)" :options="chartOptions" :series="chartSeries" :key="tab + chartKey" />
          <div class="table-responsive mt-2">
            <table class="table table-sm mb-0">
              <thead class="table-head">
                <tr>
                  <th class="table-th">#</th><th class="table-th">{{ tab === "drugs" ? "Drug" : "Service" }}</th>
                  <th class="table-th text-right">Times claimed</th><th class="table-th text-right">Claims</th><th class="table-th text-right">Facilities</th>
                  <th v-if="tab === 'drugs'" class="table-th text-right" title="Dose × frequency × days">Quantity</th>
                  <th class="table-th text-right">Avg per line</th><th class="table-th text-right">Claimed</th><th class="table-th text-right">Approved</th><th class="table-th text-right">Share</th>
                </tr>
              </thead>
              <tbody>
                <tr class="table-row" v-for="(r, i) in current.rows" :key="r.type + r.id">
                  <td class="text-muted">{{ i + 1 }}</td>
                  <td><b>{{ r.name }}</b><div class="small text-muted">{{ [r.code, r.detail].filter(Boolean).join(" · ") }}</div></td>
                  <td class="text-right">{{ num(r.lines) }}</td>
                  <td class="text-right">{{ num(r.claims) }}</td>
                  <td class="text-right">{{ num(r.facilities) }}</td>
                  <td v-if="tab === 'drugs'" class="text-right">{{ num(r.quantity) }}</td>
                  <td class="text-right">{{ money(r.average_line) }}</td>
                  <td class="text-right">{{ money(r.amount_claimed) }}</td>
                  <td class="text-right">{{ money(r.amount_approved) }}</td>
                  <td class="text-right">{{ r.share_pct === null ? "—" : r.share_pct + "%" }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </template>
      </template>
    </div>
  </div>
</template>

<script>
/**
 * Top services and drugs claimed (POST claims/statistics/items), for the Claims Statistics page.
 * Uses the page's filters; reloads when refreshKey changes (the page's "Get Statistics").
 */
export default {
  name: "ClaimItemsStats",
  props: {
    from: { type: String, default: "" },
    to: { type: String, default: "" },
    sector: { type: [Array, String], default: () => [] },
    providerId: { type: [Number, String], default: null },
    claimStatus: { type: [String, Number], default: "" },
    phcType: { type: String, default: "" },
    phcGeneral: { type: String, default: "" },
    refreshKey: { type: Number, default: 0 },
  },
  data() {
    return { tab: "services", sort: "amount", limit: 20, data: null, loading: false, exporting: false, error: "", chartKey: 0 };
  },
  computed: {
    current() { return this.data ? this.data[this.tab] : null; },
    totals() { return this.current.totals || {}; },
    chartRows() { return this.current.rows.slice(0, 20); },
    chartSeries() {
      if (this.sort === "lines" || this.sort === "claims") {
        const key = this.sort === "lines" ? "lines" : "claims";
        return [{ name: this.sort === "lines" ? "Times claimed" : "Claims", data: this.chartRows.map((r) => r[key]) }];
      }
      return [
        { name: "Claimed", data: this.chartRows.map((r) => r.amount_claimed) },
        { name: "Approved", data: this.chartRows.map((r) => r.amount_approved) },
      ];
    },
    chartOptions() {
      const money = this.sort === "amount" || this.sort === "approved";
      const fmt = (v) => (money ? this.money(v) : this.num(v));
      return {
        chart: { toolbar: { show: true } },
        colors: money ? ["#2563eb", "#059669"] : [this.tab === "drugs" ? "#7c3aed" : "#0891b2"],
        plotOptions: { bar: { horizontal: true, barHeight: "75%", borderRadius: 3 } },
        xaxis: { categories: this.chartRows.map((r) => (r.name.length > 40 ? r.name.slice(0, 38) + "…" : r.name)), labels: { formatter: fmt } },
        dataLabels: { enabled: true },
        legend: { position: "top" },
        tooltip: { shared: true, intersect: false, y: { formatter: fmt } },
      };
    },
  },
  watch: { refreshKey() { this.load(); } },
  mounted() { this.load(); },
  methods: {
    params(extra = {}) {
      return {
        from: this.from || undefined, to: this.to || undefined, sector: Array.isArray(this.sector) ? (this.sector.length ? this.sector : undefined) : (this.sector ? [this.sector] : undefined),
        provider_id: this.providerId || undefined, claim_status: this.claimStatus || undefined,
        phc_type: this.phcType || undefined, phc_general: this.phcGeneral || undefined, sort: this.sort, limit: this.limit, ...extra,
      };
    },
    load() {
      this.loading = true;
      this.error = "";
      this.axios.post("/api/v1/auth/claims/statistics/items", this.params())
        .then((res) => { this.data = res.data.data; this.chartKey++; })
        .catch((e) => { this.error = (e.response && e.response.data && e.response.data.message) || "Could not load services and drugs."; })
        .finally(() => { this.loading = false; });
    },
    exportCsv() {
      this.exporting = true;
      this.axios.post("/api/v1/auth/claims/statistics/items", this.params({ format: "csv", limit: 500 }), { responseType: "blob" })
        .then((res) => {
          const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv;charset=utf-8;" }));
          const a = document.createElement("a");
          a.href = url;
          a.download = `claims-top-services-drugs-${new Date().toISOString().slice(0, 10)}.csv`;
          document.body.appendChild(a);
          a.click();
          a.remove();
          window.URL.revokeObjectURL(url);
        })
        .catch(() => this.$toasted.error("Could not export.", { position: "top-center", duration: 3000 }))
        .finally(() => { this.exporting = false; });
    },
    num(v) { return Number(v || 0).toLocaleString(); },
    money(v) { return "₦" + Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 }); },
  },
};
</script>

<style scoped>
@import "../../reports/reports.css";
</style>
